import { ESignalQuality } from '../infra/access-point-edge-hub.service';

export interface EdgeNetInfoInterface {
  Name?: string;
  Up?: boolean;
  Mac?: string;
  Ipv4?: string;
  Ipv6?: string;
  SpeedMbps?: number;
}

export interface EdgeNetInfo {
  LocalIpv4?: string;
  localIpv4?: string;
  LocalIpv6?: string;
  localIpv6?: string;
  Gateway?: string;
  gateway?: string;
  DnsServers?: string[];
  dnsServers?: string[];
  Mac?: string;
  mac?: string;
  WifiSsid?: string;
  wifiSsid?: string;
  WifiRssi?: number;
  wifiRssi?: number;
  LinkSpeedMbps?: number;
  linkSpeedMbps?: number;
  Hostname?: string;
  hostname?: string;
  Interfaces?: EdgeNetInfoInterface[];
  interfaces?: EdgeNetInfoInterface[];
  PublicIp?: string;
  publicIp?: string;
}

export type EdgeStatus = 'online' | 'offline' | 'unknown';

export interface GatePassage {
  edgeId: string;
  logicalDirection: number;
  physicalDirection: number;
  passageCompleted: boolean;
  isTriggeredByRelease: boolean;
  releaseRequestId: string | null;
  ts: string | number;
  payload: {
    logicalDirection: number;
    physicalDirection: number;
    releaseRequestId: string | null;
    isTriggeredByRelease: boolean;
    passageCompleted: boolean;
    payloadType: number;
    version: string | null;
    requestId: string;
  };
}

export interface EdgeItem {
  EdgeId: string;
  Status: EdgeStatus;
  LastTs: string;
  Net?: EdgeNetInfo;
  ApiKeyKind?: 0 | 1;
  LastGatePassage?: GatePassage;
  SignalQuality?: keyof typeof ESignalQuality;
  AccessPointName?: string;
}

export interface IpifyResponse {
  ip: string;
}

export interface EdgeDeviceResponse {
  edgeId?: string;
  EdgeId?: string;
  apiKeyKind?: 0 | 1;
  ApiKeyKind?: 0 | 1;
}

export type HubConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'reconnecting';

export type EdgeProvisioningStateEnum =
  | 'Pending'
  | 'Provisioned'
  | 'Failed'
  | string;

export enum DirectionControlEnum {
  Blocked = 0,
  Free = 1,
  Controlled = 2,
}

export enum PassagePhysicalDirectionEnum {
  Clockwise = 0,
  Counterclockwise = 1,
  SideAtoB = 0, // Alias for Clockwise
  SideBtoA = 1, // Alias for Counterclockwise
}

export enum BoardTypeEnum {
  LiteNet2 = 1,
  LiteNet3 = 2,
  LiteNet1 = 3,
  Other = 9,
}

/**
 * Nome comercial da placa, por `BoardTypeEnum`.
 *
 * A 1.ª geração é vendida sem numeral — "Toletus LiteNet", não "LiteNet 1" —,
 * por isso o rótulo não se deriva do nome do membro do enum.
 *
 * São nomes de produto, não texto traduzível: ao contrário de
 * `signalQualityDescriptions`, o valor aqui é literal e não chave i18n.
 *
 * `Other` fica de fora de propósito: não há nome comercial para uma placa que o
 * backend não reconhece, e inventar um faria a UI afirmar um modelo que não
 * existe. Quem consome trata a ausência como "modelo desconhecido".
 */
export const BOARD_TYPE_LABELS: Partial<Record<BoardTypeEnum, string>> = {
  [BoardTypeEnum.LiteNet1]: 'Toletus LiteNet',
  [BoardTypeEnum.LiteNet2]: 'Toletus LiteNet 2',
  [BoardTypeEnum.LiteNet3]: 'Toletus LiteNet 3',
};

export enum AccessPointBackendTypeEnum {
  LocalBoard = 0,
  ExternalController = 1,
}

/**
 * Converts enum values from OData response (which may come as string keys) to numeric values
 * @param enumObj - The enum object to use for conversion
 * @param value - The value to convert (can be string key or number)
 * @returns The numeric value of the enum, or the original value if conversion fails
 */
export function convertEnumToNumber<T extends Record<string, string | number>>(
  enumObj: T,
  value: string | number | undefined | null
): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const numericValue = Number(value);
    if (!isNaN(numericValue) && isFinite(numericValue)) {
      return numericValue;
    }

    const enumValue = enumObj[value as keyof T];
    if (typeof enumValue === 'number') {
      return enumValue;
    }
  }

  return typeof value === 'number' ? value : Number(value);
}

export enum BiometricReaderType {
  DigitalPersona = 0,
  Futronic = 1,
}

export interface BiometricReader {
  InstanceId: string;
  Name: string;
  Type: BiometricReaderType;
}

/**
 * Metade do display onde o anúncio aparece no modo tela dividida — o
 * `AdvertisementPositionEnum` do accessdirectoryservice-api, que grava e
 * devolve o número. Pontos anteriores ao campo vêm como `Top`, o padrão da
 * coluna e o comportamento de antes da opção existir.
 */
export enum AdvertisementPositionEnum {
  Top = 0,
  Bottom = 1,
}

export interface AccessPoint {
  AccessPointId: string;
  EquipmentId: string | null;
  Name: string;
  IsActive: boolean;
  IsLegacy?: boolean;
  BackendTypeEnum: number;
  CameraDevices: TCameraDevice[];
  EntryMode: number; // DirectionControlEnum
  ExitMode: number; // DirectionControlEnum
  EntryDirection: number; // PassagePhysicalDirectionEnum
  IsSplitScreenMode: boolean;
  ShowAdvertisement: boolean;
  AdvertisementPosition?: AdvertisementPositionEnum;
  ReleaseDurationSeconds: number;
  GateNumber: number | null;
  EdgeId: string; // derived from equipment
  /** Ausente nos caminhos create/update, que não devolvem o modelo da placa. */
  BoardType?: BoardTypeEnum | null;
  Status: EdgeStatus; // 'online' | 'offline' | 'unknown'
  LastTs: string;
  /**
   * O último batimento como o servidor o mandou. O `LastTs` cai na hora da
   * carga quando ele falta, e aí não serve para dizer "sem contato desde".
   * Ausente nos caminhos create/update.
   */
  EdgeLastSeenUtc?: string | null;
  SignalQuality?: keyof typeof ESignalQuality;
  BiometricReader?: BiometricReader | null;
}

export interface CreateAccessPointRequest {
  EquipmentId: string | null;
  Name: string;
  IsActive: boolean;
  BackendTypeEnum: number;
  CameraDevices: TCameraDevice[];
  EntryMode: number;
  ExitMode: number;
  EntryDirection: number;
  ReleaseDurationSeconds: number;
  IsSplitScreenMode: boolean;
  ShowAdvertisement: boolean;
  AdvertisementPosition?: AdvertisementPositionEnum;
  BiometricReader?: BiometricReader | null;
}

export interface UpdateAccessPointRequest {
  AccessPointId: string;
  EquipmentId?: string | null;
  Name?: string;
  IsActive?: boolean;
  BackendTypeEnum?: number;
  EntryMode?: number;
  ExitMode?: number;
  EntryDirection?: number;
  ReleaseDurationSeconds?: number;
  IsSplitScreenMode?: boolean;
  ShowAdvertisement?: boolean;
  AdvertisementPosition?: AdvertisementPositionEnum;
  CameraDevices?: TCameraDevice[];
  BiometricReader?: BiometricReader | null;
}

/**
 * Registo de *push de configuração* para o edge (traz `ConfigurationJson`,
 * `RetryCount`, `AppliedOnUtc`), devolvido por `GET /AccessPoint?id=`.
 *
 * Não é telemetria: não carrega `IsActive` nem `LastSeenUtc`. O estado do edge
 * vem de `AccessPointListItem.EdgeIsActive` / `EdgeLastSeenUtc`.
 */
export interface EdgeConfigurationDeployment {
  EdgeConfigurationDeploymentId?: string;
  EdgeId?: string;
  EquipmentId?: string;
  [key: string]: any; // Allow additional properties from API
}

export interface AccessPointResponse {
  AccessPointId: string;
  EquipmentId: string | null;
  Name: string;
  IsActive: boolean;
  BackendTypeEnum: number | string; // OData may return enum as string key
  CameraDevices: TCameraDevice[] | null;
  EntryMode: number | string; // OData may return enum as string key
  ExitMode: number | string; // OData may return enum as string key
  EntryDirection: number | string; // OData may return enum as string key
  IsSplitScreenMode: boolean;
  ShowAdvertisement: boolean;
  AdvertisementPosition?: number | string; // OData may return enum as string key
  ReleaseDurationSeconds: number;
  GateNumber: number | null;
  EdgeConfigurationDeployment?: EdgeConfigurationDeployment | null;
  IsLegacy?: boolean;
  BiometricReader?: BiometricReader | null;
  [key: string]: any; // Allow additional properties from API
}

/**
 * Item de `GET /OData/v2/AccessPoints` (accessdirectoryservice-api).
 *
 * Difere do `AccessPointResponse` (REST `/AccessPoint`) em três pontos, e por
 * isso é um tipo à parte em vez de mais um campo opcional no mesmo:
 *
 * - devolve **array nu**, sem envelope `{ value }` e sem `@odata.context`;
 * - serializa os enums por **número** (`EntryMode: 2`), não por nome;
 * - achata o edge no próprio item (`EdgeId`, `EdgeIsActive`, `EdgeLastSeenUtc`,
 *   `BoardType`) em vez de o enterrar no `EdgeConfigurationDeployment`.
 *
 * `EdgeIsActive` é flag de configuração, **não** presença: há pontos com
 * `EdgeIsActive: true` e `EdgeLastSeenUtc` de meses atrás. O batimento real é
 * o `EdgeLastSeenUtc`.
 *
 * Aceita `$filter`, `$select`, `$top` e `$orderby`; não honra `$count`.
 */
export interface AccessPointListItem {
  AccessPointId: string;
  TenantId: string;
  EquipmentId: string | null;
  Name: string;
  IsActive: boolean;
  IsLegacy: boolean;
  BackendTypeEnum: AccessPointBackendTypeEnum;
  EntryMode: DirectionControlEnum;
  ExitMode: DirectionControlEnum;
  EntryDirection: PassagePhysicalDirectionEnum;
  IsSplitScreenMode: boolean;
  ShowAdvertisement: boolean;
  AdvertisementPosition?: AdvertisementPositionEnum;
  ReleaseDurationSeconds: number;
  GateNumber: number | null;
  /** `null` nos pontos legados, que não têm edge associado. */
  EdgeId: string | null;
  EdgeIsActive: boolean | null;
  EdgeLastSeenUtc: string | null;
  BoardType: BoardTypeEnum | null;
  CameraDevices: TCameraDevice[];
  BiometricReader: BiometricReader | null;
}

export enum ELogicalDirectionEnum {
  Entry = 1,
  Exit = 2,
  Both = 3,
}

export enum AccessEvalStatus {
  Allowed = 'Allowed',
  Denied = 'Denied',
  NotIdentified = 'NotIdentified',
  Error = 'Error',
}

export interface AccessEvalResult {
  edgeId: string;
  status: AccessEvalStatus;
  isAllowed: boolean;
  logicalDirection: string;
  personId: string;
  name: string | null;
  message: string | null;
  effectiveUntilUtc: string;
  logs: any;
  error?: string | null;
  requestId: string;
  ts: string;
  /** Access point name from hub payload (e.g. for anonymous passage). */
  accessPoint?: string;
}

export enum ECamType {
  Other,
  Webcam,
  IdFace,
  Intelbras,
  Embedded,
  Hikvision,
  AiFace,
}

/** Camera types addressed over the network, which carry IP, credentials and streaming. */
export const IP_CAM_TYPES = [
  ECamType.Intelbras,
  ECamType.IdFace,
  ECamType.Hikvision,
];

export enum AccessMethodEnum {
  Bluetooth = 0,
  Card = 1,
  Face = 2,
  Fingerprint = 3,
  Keyboard = 4,
  PersonId = 5,
  QRCode = 6,
  F2 = 7,
  F3 = 8,
}

export enum AccessIntentEnum {
  Unknown = 0,
  Entering = 1,
  Exiting = 2,
}

export enum CodeIdentifierEnum {
  PersonCode = 0,
  AccessPin = 1,
}

export interface EvaluateByPersonIdRequest {
  Identifier: string;
  AccessMethod: AccessMethodEnum;
  AccessIntent: AccessIntentEnum;
}

/**
 * Resposta do POST AccessEvaluation/EvaluateByPersonId. O corpo do erro tem a
 * mesma forma: o 400 (regras recusaram) traz o `AttemptId` para a liberação
 * sobre recusa, e o 429 o `RemainingTtl` em milissegundos.
 */
export interface EvaluateByPersonIdResponse {
  Response?: {
    LogicalDirection?: number;
    AttemptId?: string;
    RemainingTtl?: number;
    Message?: string;
  };
}

/** Response from GET AccessEvaluation/SimulateAccess. */
export interface SimulateAccessResponse {
  AccessDenialReason?: number;
  ConsumptionId?: string;
  EffectiveUntilUtc?: string;
  IsAllowed?: boolean;
  IsBirthday?: boolean;
  IsGuardian?: boolean;
  IsGympass?: boolean;
  IsTotalpass?: boolean;
  LogicalDirection?: string;
  Logs?: string;
  Message?: string;
  Name?: string;
  PersonId?: string;
  ProfilePhotoUrl?: string;
  SubscriptionId?: string;
  SubscriptionName?: string;
  Token?: string;
}

export type TCameraDevice = {
  CameraDeviceId?: string;
  EquipmentId?: string;
  AccessIntent?: AccessIntentEnum;
  Name?: string;
  IsActive?: boolean;
  Type?: ECamType;
  Ip?: string | null;
  Login?: string | null;
  Password?: string | null;
  /**
   * Label serial of the device. The AIFace opens the connection against the desk
   * and identifies itself by this number, so it is required when `Type` is
   * `AiFace` and ignored for every other type.
   */
  SerialNumber?: string | null;
  /** Only applicable to IP-camera devices (Type ∈ {Intelbras, IdFace, Hikvision}). */
  UseVideoStreaming?: boolean;
  RtspPort?: number | null;
  TenantId?: string;
  AddedBy?: string;
  AddedOnUtc?: string;
  UpdatedBy?: string;
  UpdatedOnUtc?: string;
  DeletedBy?: string | null;
  DeletedOnUtc?: string | null;
  Index?: number;
  InstanceId?: string;
};

/** Edge event entry from GET /EdgeEvents/Get (accessedgemanager-api). */
export interface IEdgeEventEntry {
  TenantId: string;
  EdgeId: string;
  Type: number;
  Level: number;
  OccurredOnUtc: string;
  PersonId: string;
  Allowed: boolean;
  Code: string;
  Message: string;
  CorrelationId: string | null;
  PayloadJson?: string;
}

/** Edge event payload from GET /EdgeEvents/Get. */
export interface IEdgeEventPayload {
  AccessPointId?: string;
  /** Display name (e.g. manual release, anonymous passage). */
  AccessPoint?: string;
  AccessPointName?: string;
  TriggerKind?: string;
  /** Person who released (ManualAnonymous, ManualOverrideDenied). Use getOwnerInfo when tenantId === UserPersonId. */
  UserPersonId?: string | null;
  CredentialMethod?: string;
  Intent?: string;
  Identifier?: string;
  Allowed?: boolean;
  /** Reason code when access is denied (e.g. 0). */
  DeniedReason?: number;
  /** Id of the access attempt; used for override-denied API. */
  AttemptId?: string;
  /** Time (in seconds) the override-denied button stays available; shown as countdown on the button. */
  AttemptTtlSeconds?: number;
  ManualReleaseBlocked?: boolean;
  ManualReleaseReasonRequired?: boolean;
  Name?: string;
  /** URL path to the profile photo; used for avatar display when available. */
  ProfilePhotoUrl?: string;
  Message?: string;
  EffectiveUntilUtc?: string | null;
  Birthday?: boolean;
  RemainingTtl?: number;
  LatencyMs?: number;
  SummaryKey?: string;
  /** Summary text for live access activity (person + action + access point). */
  SummaryActivity?: string;
  ReleaseKind?: string;
  ControlOutcome?: string;
  LogicalDirection?: string;
  SummaryText?: string;
  SummaryContextText?: string;
  LogsPreview?: string;
  /** HeartbeatTelemetry: previous health status. */
  From?: string;
  /** HeartbeatTelemetry: current health status (e.g. Healthy, Degraded, Offline). */
  To?: string;
  /** HeartbeatTelemetry: reason code. */
  Reason?: string;
  /** HeartbeatTelemetry: details (e.g. LastRttMs, AvgRttMs). */
  Details?: { LastRttMs?: number; AvgRttMs?: number };
  /** HeartbeatTelemetry: connection quality from (Excellent, Good, Fair, Poor). */
  FromQuality?: string;
  /** HeartbeatTelemetry: connection quality to (Excellent, Good, Fair, Poor). */
  ToQuality?: string;
  /** HeartbeatTelemetry: pre-translated summary (e.g. "Conexão de Recepção está boa"). */
  Summary?: string;
  /** HeartbeatTelemetry: tooltip text for the telemetry icon (e.g. event log details). */
  SummaryTooltip?: string;
  /**
   * Campos de um acesso decidido na placa (códigos `*_ON_EDGE*`). Ausentes nas
   * decisões da nuvem — ausência de `DecisionSource` quer dizer nuvem.
   */
  DecisionSource?: EdgeDecisionSource;
  /** Por que o Híbrido decidiu na placa; `null` = placa em LocalOnly. */
  FallbackReason?: EdgeFallbackReason | null;
  /** O que a nuvem teria decidido; `null` = não foi consultada (o caso comum, não erro). */
  CloudWouldAllow?: boolean | null;
  /** `true` só quando a placa liberou, a pessoa passou e a nuvem, consultada, teria negado. */
  Diverged?: boolean;
  /** Quando a nuvem avaliou — não é a hora do acesso. */
  EvaluatedAsOf?: string | null;
  /** Nota em pt-BR escrita pelo AEM ("relato tardio", "passagem desconhecida", …). */
  ReportNote?: string | null;
  EdgeResult?: string;
  EdgeDenyReason?: string | null;
  Passage?: EdgePassage;
  Method?: string;
  BiometricMatchScore?: number | null;
  /** Id do evento na placa; é o mesmo `AccessLogId` do histórico de acessos. */
  EventId?: string;
  /** Mensagem da nuvem quando foi consultada (numa divergência, o motivo da negação). */
  CloudMessage?: string | null;
}

/** Quem decidiu o acesso. No histórico (OData) é o enum `AccessDecisionSource`, serializado pelo nome. */
export type EdgeDecisionSource = 'Cloud' | 'Edge';

/** Motivo de o Híbrido cair para a decisão local. Texto livre no contrato; estes são os valores conhecidos. */
export type EdgeFallbackReason =
  | 'Timeout'
  | 'BreakerOpen'
  | 'Error'
  | 'Disconnected'
  | (string & {});

export type EdgePassage = 'Completed' | 'NotUsed' | 'Unknown' | (string & {});

/** Single item from GET /EdgeEvents/Get response. */
export interface IEdgeEventResponse {
  id: string;
  entry: IEdgeEventEntry;
  payload: IEdgeEventPayload;
}

/** Query params for GET /EdgeEvents/Get. */
export interface IEdgeEventsGetParams {
  take?: number;
  edgeId?: string;
  beforeId?: string;
}

export interface ISpaceAccessPoint {
  SpaceId?: string;
  AccessPointId?: string;
  Description?: string;
  TenantId?: string;
}

/**
 * Espelha `Toletus.AccessEdgeManager.Shared.Enums.Actions` do accessedgemanager-api.
 * A ordem importa: `Activate` é o primeiro membro, logo vale 0, e é o valor que o
 * controller assume quando o parâmetro não chega.
 */
export enum EEdgeDeviceAction {
  Activate = 0,
  Deactivate = 1,
}

/**
 * Etapas do cadastro de digital embarcada, na ordem em que chegam.
 *
 * Vocabulário **fechado** — é enumeração do backend, não texto livre. Cada etapa
 * traz um `percent` fixo, e é ele que alimenta a barra e os segmentos do Lottie:
 * `sensor-preparing` 0, `reading-1` 33, `reading-2` 66, `reading-3` 100.
 *
 * A preparação do sensor pode levar ~25s, o que faz uma reconexão do hub a meio
 * do cadastro ser um caso realista e não teórico.
 */
export type EmbeddedEnrollStage =
  | 'sensor-preparing'
  | 'reading-1'
  | 'reading-2'
  | 'reading-3';

/** `percent` que o backend envia com cada etapa, para quando o evento vier sem ele. */
export const EMBEDDED_ENROLL_STAGE_PERCENT: Record<EmbeddedEnrollStage, number> = {
  'sensor-preparing': 0,
  'reading-1': 33,
  'reading-2': 66,
  'reading-3': 100,
};

/**
 * Payload do evento `progress` do `EdgeRealtimeHub`, emitido durante um cadastro
 * de digital embarcada.
 *
 * `corrId` é o `RequestId` que o front gerou e enviou no POST — **quem consome
 * TEM de filtrar por ele**. Pode haver mais de um cadastro em curso no mesmo
 * equipamento, e o que não for seu deve ser ignorado. `corrId` vazio também se
 * ignora: é evento de outra natureza, não erro.
 *
 * **A caixa aqui é camelCase de propósito, e não é incoerência com o resto.**
 * O corpo REST deste serviço é PascalCase, mas os eventos do hub chegam em
 * camelCase — é o que os outros oito payloads deste mesmo ficheiro
 * (`edgeId`, `signalQuality`, `logicalDirection`) já fazem em produção. São
 * dois canais com dois regimes de serialização.
 *
 * `payload` é o evento cru serializado pelo relay. Não interpretar: o que vale é
 * `stage`/`percent`, e a autoridade final é a resposta do POST.
 */
export interface EdgeEnrollProgressPayload {
  edgeId?: string;
  corrId?: string;
  stage?: EmbeddedEnrollStage;
  percent?: number;
  payload?: string;
  ts?: string;
}

/**
 * Diz se o equipamento tem sensor de digital na própria placa.
 *
 * Responde *"este equipamento **pode** cadastrar sem leitor USB?"* — e é só isso.
 * Quem decide por **qual** caminho o cadastro corre é a presença de
 * `BiometricReader` no ponto de acesso: com leitor, vai pelo fingerprintservice
 * como sempre foi; sem leitor, vai pelo accessdirectoryservice.
 *
 * `null`/`undefined` devolve `false` de propósito: o campo é opcional nos
 * caminhos de create/update, que não devolvem o modelo da placa, e um ponto
 * legado não tem edge nenhum. Na dúvida, o comportamento antigo.
 */
export function supportsEmbeddedEnrollment(
  boardType: BoardTypeEnum | number | null | undefined
): boolean {
  return boardType === BoardTypeEnum.LiteNet2;
}

/**
 * Equipamento como o cartão da grade de pontos de acesso o consome.
 *
 * Vive aqui, e não no `*.component.ts`, porque tipo declarado no ficheiro de um
 * componente vaza pelo `export *` do barrel e amarra quem o consome ao
 * componente. As entidades irmãs (`EdgeItem`, `AccessPoint`) já moram neste
 * ficheiro.
 */
export interface AccessPointCardDevice {
  EdgeId: string;
  Status: string;
  IsAccessPoint: boolean;
  AccessPointId?: string | null;
  Name?: string;
  SignalQuality?: keyof typeof ESignalQuality;
  IsLegacy?: boolean;
  CameraDevices?: TCameraDevice[];
  BoardType?: BoardTypeEnum | null;
  /**
   * MAC do equipamento, quando conhecido.
   *
   * Chega de forma oportunista pelo evento `netInfo` do hub, e por isso pode
   * simplesmente não existir — um equipamento que ainda não reportou a rede não
   * tem MAC nenhum a mostrar.
   */
  MacAddress?: string | null;
  /**
   * Configuração do ponto de acesso, que o cartão mostra na faixa de ícones,
   * nos badges de fluxo e no tooltip. Só existe nos pontos configurados: um edge
   * sem ponto de acesso não tem nada disto, e o legado não mostra.
   */
  EntryMode?: DirectionControlEnum;
  ExitMode?: DirectionControlEnum;
  EntryDirection?: PassagePhysicalDirectionEnum;
  ReleaseDurationSeconds?: number;
  IsSplitScreenMode?: boolean;
  ShowAdvertisement?: boolean;
  BiometricReader?: BiometricReader | null;
}

export const MANUAL_RELEASE_REASON_MAX_LENGTH = 500;

export const MANUAL_RELEASE_ERROR_CODES = {
  notAllowed: 'manual-release.not-allowed',
  reasonRequired: 'manual-release.reason-required',
  reasonTooLong: 'manual-release.reason-too-long',
  anonymousBlocked: 'manual-release.anonymous-blocked',
} as const;

export type TManualReleaseErrorCode =
  (typeof MANUAL_RELEASE_ERROR_CODES)[keyof typeof MANUAL_RELEASE_ERROR_CODES];

export interface IManualReleaseErrorBody {
  code?: string;
  message?: string;
  EdgeId?: string;
  AttemptId?: string;
}

export function withManualReleaseFlags<T>(payload: T): T {
  const bag = payload as Record<string, unknown> | null;

  if (!bag) return payload;

  for (const flag of ['ManualReleaseBlocked', 'ManualReleaseReasonRequired'] as const) {
    const camel = flag.charAt(0).toLowerCase() + flag.slice(1);

    if (bag[flag] === undefined && bag[camel] !== undefined) {
      bag[flag] = bag[camel];
    }
  }

  return payload;
}

export function manualReleaseErrorCode(error: unknown): TManualReleaseErrorCode | null {
  const body = (error as { error?: IManualReleaseErrorBody } | null)?.error;
  const code = body?.code;
  const known = Object.values(MANUAL_RELEASE_ERROR_CODES) as readonly string[];

  return code && known.includes(code) ? (code as TManualReleaseErrorCode) : null;
}
